import { useMemo } from 'react'
import { getDefaultMapCenter } from '../../utils/mapUtils'

/**
 * useMapConfiguration - Hook for calculating map configuration values
 * Consolidates map center, zoom, and bounds calculations
 * 
 * @param {Object} params - Configuration parameters
 * @param {boolean} params.isEntityMode - Whether map is in entity detail mode
 * @param {Object} params.entityLocation - Entity location {lat, lng}
 * @param {Object} params.userLocation - User location {lat, lng}
 * @param {Object} params.searchLocation - Search location {lat, lng}
 * @param {Array} params.entities - Array of entities to display
 * @param {Object} params.activeMapLocation - Computed active location
 * @returns {Object} Map configuration with center, zoom, and bounds
 */
export function useMapConfiguration({
  isEntityMode = false,
  entityLocation = null,
  userLocation = null,
  searchLocation = null,
  entities = [],
  activeMapLocation = null
}) {
  // Calculate default center for initial map load
  const defaultCenter = useMemo(() => getDefaultMapCenter(), [])

  // Calculate map center based on mode and available locations
  const mapCenter = useMemo(() => {
    // Entity mode: prioritize entity location
    if (isEntityMode && entityLocation) {
      if (Array.isArray(entityLocation)) {
        return entityLocation
      }
      return [
        entityLocation.lat || entityLocation.latitude,
        entityLocation.lng || entityLocation.longitude
      ]
    }

    // Use active map location (priority: userLocation > first entity)
    if (activeMapLocation) {
      return [activeMapLocation.lat, activeMapLocation.lng]
    }

    // Fallback to default
    return defaultCenter
  }, [isEntityMode, entityLocation, activeMapLocation, defaultCenter])

  // Calculate zoom level based on context
  const mapZoom = useMemo(() => {
    if (isEntityMode && entityLocation) {
      return 14 // Closer zoom for entity detail
    }
    if (userLocation || searchLocation) {
      return 13 // Medium zoom for user/search locations
    }
    if (entities.length > 0) {
      return 12 // Medium-wide zoom when entities present
    }
    return 11 // Default city-level zoom
  }, [isEntityMode, entityLocation, userLocation, searchLocation, entities.length])

  // Calculate bounds for fitting multiple entities
  const mapBounds = useMemo(() => {
    if (!entities || entities.length === 0) return null

    const validEntities = entities.filter(e => 
      (e.latitude || e.currentLatitude) && 
      (e.longitude || e.currentLongitude)
    )

    if (validEntities.length === 0) return null

    const lats = validEntities.map(e => e.latitude || e.currentLatitude)
    const lngs = validEntities.map(e => e.longitude || e.currentLongitude)

    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    }
  }, [entities])

  return {
    defaultCenter,
    mapCenter,
    mapZoom,
    mapBounds
  }
}
